import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Edit,
  Trash2,
  GripVertical,
  Home,
  Star,
  Edit3,
  Rocket,
  MoreHorizontal,
} from 'lucide-react';
import type { Page } from '../schemas/validation';
import { cn } from '../utils/cn';

type DisplayPublishStatus = 'draft' | 'unpublished_changes' | 'published';

interface DraggablePageCardProps {
  page: Page;
  siteId: string;
  onDelete: (page: Page) => void;
  onEdit?: (page: Page) => void;
  onToggleHomePage?: (page: Page) => void;
  onPublish?: (page: Page) => void;
  isPublishing?: boolean;
  backendPublishStatus?: 'never_published' | 'changed' | 'up_to_date';
}

function toDisplayStatus(
  backend?: 'never_published' | 'changed' | 'up_to_date'
): DisplayPublishStatus {
  if (!backend || backend === 'never_published') return 'draft';
  if (backend === 'changed') return 'unpublished_changes';
  return 'published';
}

export const DraggablePageCard: React.FC<DraggablePageCardProps> = ({
  page,
  siteId,
  onDelete,
  onEdit,
  onToggleHomePage,
  onPublish,
  isPublishing,
  backendPublishStatus,
}) => {
  const publishStatus = toDisplayStatus(backendPublishStatus);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: page.id });

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'card group relative min-w-0 cursor-default',
        isDragging && 'opacity-30 shadow-2xl scale-105 z-50',
        isMoreOpen && !isDragging && 'z-40',
        isOver && !isDragging && 'ring-2 ring-primary-500 ring-opacity-50 bg-primary-50/50',
        'transition-all duration-200'
      )}
    >
      {isOver && !isDragging && (
        <div className="absolute inset-0 border-2 border-dashed border-primary-400 rounded-lg bg-primary-50/30 flex items-center justify-center z-10">
          <div className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium">
            Drop here
          </div>
        </div>
      )}

      <div className="p-4 relative min-w-0">
        {/* Header: drag handle + title + home badge + status */}
        <div className="flex min-w-0 items-center justify-between gap-2 mb-1.5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              className={cn(
                'p-0.5 text-gray-400 hover:text-primary-600 rounded transition-colors shrink-0',
                'opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing',
                'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500',
                isDragging && 'opacity-100 text-primary-600'
              )}
              {...attributes}
              {...listeners}
              title="Drag to reorder"
            >
              <GripVertical size={14} />
            </button>
            <h3
              className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900"
              title={page.title}
            >
              {page.title}
            </h3>
            {page.isHomePage && (
              <span className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0">
                <Home size={10} />
                Home
              </span>
            )}
          </div>
          {publishStatus === 'draft' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 shrink-0">
              Draft
            </span>
          )}
          {publishStatus === 'unpublished_changes' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800 shrink-0">
              Unsaved
            </span>
          )}
          {publishStatus === 'published' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800 shrink-0">
              Published
            </span>
          )}
        </div>

        {/* Slug */}
        <p className="text-xs text-gray-500 truncate mb-3 pl-6">/{page.slug}</p>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Link
            to={`/sites/${siteId}/pages/${page.id}/editor`}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 text-xs"
          >
            <Edit size={13} />
            Edit Page
          </Link>

          {onPublish && publishStatus !== 'published' && (
            <button
              onClick={() => onPublish(page)}
              disabled={isPublishing}
              className="inline-flex items-center justify-center p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              title={publishStatus === 'draft' ? 'Publish page' : 'Republish page'}
            >
              {isPublishing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Rocket size={15} />
              )}
            </button>
          )}

          {/* Overflow menu */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              className={cn(
                'p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors',
                'sm:opacity-0 sm:group-hover:opacity-100'
              )}
              aria-expanded={isMoreOpen}
              aria-haspopup="true"
            >
              <MoreHorizontal size={16} />
            </button>

            {isMoreOpen && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {onEdit && (
                  <button
                    onClick={() => {
                      setIsMoreOpen(false);
                      onEdit(page);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Edit3 size={16} className="mr-3 text-blue-600" />
                    Edit Page Name
                  </button>
                )}
                {onToggleHomePage && !page.isHomePage && (
                  <button
                    onClick={() => {
                      setIsMoreOpen(false);
                      onToggleHomePage(page);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Star size={16} className="mr-3 text-amber-600" />
                    Set as Home Page
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsMoreOpen(false);
                    onDelete(page);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={16} className="mr-3" />
                  Delete Page
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
